import { useEffect, useState } from 'react';

const serviceTypes = [
  'Transporte Empresarial',
  'Transporte Industrial',
  'Turismo / Renta',
  'Servicios Nocturnos',
  'Turnos Mixtos',
  'Rutas Especiales',
];

const cities = ['San Luis Potosí', 'Monterrey'];

export default function QuoteSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: serviceTypes[0],
    city: cities[0],
    employees: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.quote-animate');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.company) return;

    setSubmitting(true);
    const params = new URLSearchParams();
    params.append('name', formData.name);
    params.append('email', formData.email);
    params.append('phone', formData.phone);
    params.append('company', formData.company);
    params.append('serviceType', formData.serviceType);
    params.append('city', formData.city);
    params.append('employees', formData.employees);
    params.append('message', formData.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d814jr8m1au3l14kc5bg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          serviceType: serviceTypes[0],
          city: cities[0],
          employees: '',
          message: '',
        });
        setTimeout(() => setSubmitted(false), 5000);
      }
    } catch (err) {
      console.error('Error al enviar cotización:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="cotizacion" className="py-20 md:py-32 bg-yakis-navy text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>

      <div className="section-container relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-14">
          <div className="quote-animate animate-on-scroll mb-4">
            <span className="text-yakis-gold text-sm font-semibold tracking-wider uppercase">
              Cotización Rápida
            </span>
          </div>
          <h2 className="quote-animate animate-on-scroll font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight mb-4">
            Obtén tu cotización
            <br />
            <span className="text-yakis-gold">en menos de 24 horas</span>
          </h2>
          <p className="quote-animate animate-on-scroll text-white/60 text-base md:text-lg">
            Completa el formulario y nuestro equipo de operaciones te contactará
            con una propuesta personalizada para tu empresa.
          </p>
        </div>

        {/* Quote Form */}
        <div className="quote-animate animate-on-scroll max-w-2xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-white/10">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-yakis-gold/20 mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-yakis-gold" />
                </div>
                <p className="font-display font-semibold text-xl mb-2">
                  ¡Cotización solicitada!
                </p>
                <p className="text-sm text-white/60">
                  Nuestro equipo te contactará en menos de 24 horas.
                </p>
              </div>
            ) : (
              <form
                id="cotizacion-yakis-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d814jr8m1au3l14kc5bg"
                method="POST"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="quote-name" className="block text-sm font-medium text-white mb-1.5">
                      Nombre completo
                    </label>
                    <input
                      id="quote-name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-company" className="block text-sm font-medium text-white mb-1.5">
                      Empresa
                    </label>
                    <input
                      id="quote-company"
                      type="text"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                      placeholder="Nombre de la empresa"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="quote-email" className="block text-sm font-medium text-white mb-1.5">
                      Correo electrónico
                    </label>
                    <input
                      id="quote-email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                      placeholder="correo@empresa.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-phone" className="block text-sm font-medium text-white mb-1.5">
                      Teléfono
                    </label>
                    <input
                      id="quote-phone"
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                      placeholder="444 123 4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label htmlFor="quote-service" className="block text-sm font-medium text-white mb-1.5">
                      Tipo de servicio
                    </label>
                    <select
                      id="quote-service"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all appearance-none cursor-pointer"
                    >
                      {serviceTypes.map((type) => (
                        <option key={type} value={type} className="text-yakis-navy">
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="quote-city" className="block text-sm font-medium text-white mb-1.5">
                      Ciudad
                    </label>
                    <select
                      id="quote-city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all appearance-none cursor-pointer"
                    >
                      {cities.map((city) => (
                        <option key={city} value={city} className="text-yakis-navy">
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="quote-employees" className="block text-sm font-medium text-white mb-1.5">
                      N° de empleados
                    </label>
                    <input
                      id="quote-employees"
                      type="number"
                      name="employees"
                      value={formData.employees}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                      placeholder="Ej. 150"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="quote-message" className="block text-sm font-medium text-white mb-1.5">
                    Detalles adicionales
                  </label>
                  <textarea
                    id="quote-message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all resize-none"
                    placeholder="Describe tus necesidades específicas..."
                  />
                  <p className="text-xs text-white/40 mt-1 text-right">
                    {formData.message.length}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-yakis-gold text-yakis-navy font-semibold py-4 rounded-full transition-all duration-300 hover:bg-yakis-goldLight hover:shadow-lg hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className={`ri-${submitting ? 'loader-4-line animate-spin' : 'file-list-3-line'}`} />
                  {submitting ? 'Enviando...' : 'Solicitar cotización'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}